import { Button, Progress, Spin, Tooltip, notification } from 'antd';
import clsx from 'clsx';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { EventBusEventEnum } from '../../enums/EventBusEventEnum';
import { FileUploadStatusEnum } from '../../enums/FileUploadStatusEnum';
import { useAuth } from '../../hooks/useAuth';
import useFilesActions from '../../hooks/useFilesActions';
import { useFileUpload } from '../../hooks/useFileUpload';
import useOpenFile from '../../hooks/useOpenFile';
import type File from '../../interfaces/File';
import EventBus from '../../services/EventBus';
import { copyToClipboard, formatBytes } from '../../utils/utils';
import { Icon } from '../Core/Icon';
import FileIcon from './FileIcon';
import TruncatedTooltip from './TruncatedTooltip';

const UploadProgressDrawer = () => {
  const { t } = useTranslation();
  const { authenticatedUser } = useAuth();
  const [isMinimized, setIsMinimized] = useState(false);
  const {
    uploadFilesQueue,
    isUploadingFiles,
    emptyUploadFilesQueue,
    uploadGroupedFilesQueue,
    totalGroupedFilesAmount,
    uploadedGroupedFilesAmount,
    getFailedFilesInFolder,
    handleOpenUploadingFilesErrorsModal,
  } = useFileUpload();
  const { redirectFileToLocation } = useOpenFile();

  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const uploadHistoryRef = useRef<Record<string, { time: number; bytes: number }[]>>({});
  const [prevUploadingFileId, setPrevUploadingFileId] = useState<string | null>(null);

  const params = useParams();

  const { handleShareFiles } = useFilesActions({
    folderUid: params.folderUid,
  });

  /**
   *
   * @param fileId
   * @returns
   */
  function getUploadSpeed(fileId: string): number | null {
    const history = uploadHistoryRef.current[fileId];
    if (!history || history.length < 2) return null;
    const first = history[0];
    const last = history[history.length - 1];
    if (!first || !last) return null;
    const deltaBytes = last.bytes - first.bytes;
    const deltaTime = (last.time - first.time) / 1000; // seconds
    if (deltaTime === 0) return null;
    return deltaBytes / deltaTime; // bytes/sec
  }

  function getETA(fileSize: number, uploaded: number, speed: number | null): number | null {
    if (!speed || speed === 0) return null;
    return (fileSize - uploaded) / speed; // seconds
  }

  function formatETA(seconds: number | null): string {
    if (!seconds || !isFinite(seconds)) return '';
    if (seconds < 60) return `Mancano ~${Math.ceil(seconds)}s`;
    const min = Math.floor(seconds / 60);
    const sec = Math.ceil(seconds % 60);
    return `Mancano ~${min} min${sec > 0 ? ` ${sec}s` : ''}`;
  }

  useEffect(() => {
    const uploadingFile = uploadFilesQueue.find(
      (file) => file.status === FileUploadStatusEnum.Uploading,
    );

    if (
      uploadingFile &&
      itemRefs.current[uploadingFile.id] &&
      uploadingFile.id !== prevUploadingFileId
    ) {
      // Scroll the element into view
      itemRefs?.current?.[uploadingFile.id]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setPrevUploadingFileId(uploadingFile.id);
    }
  }, [uploadFilesQueue]);

  useEffect(() => {
    if (isMinimized) {
      setIsMinimized(false);
    }
  }, [uploadGroupedFilesQueue.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      uploadFilesQueue.forEach((file) => {
        if (
          file.status === FileUploadStatusEnum.Uploading &&
          file.file &&
          file.file.size &&
          file.progress !== undefined
        ) {
          const now = Date.now();
          const bytesUploaded = Math.round(file.file.size * (file.progress / 100));
          let history = uploadHistoryRef.current[file.id];
          if (!history) {
            history = [];
            uploadHistoryRef.current[file.id] = history;
          }
          history.push({ time: now, bytes: bytesUploaded });
          uploadHistoryRef.current[file.id] = history.filter((e) => now - e.time < 10000);
        }
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [uploadFilesQueue]);

  /**
   * Title representing the current state of a process based on its status.
   *
   * This memoized value determines the appropriate label to display depending on the
   * status of processes within `processStatuses`.
   * If any process is marked as `InProgress`, it returns the label for 'downloadPreparation';
   * otherwise, it returns the label for 'downloadReady'.
   *
   * Dependencies:
   * - processStatuses: Array of process objects to check their statuses.
   */
  const title: string = useMemo(() => {
    // Check if there are no more files to upload ( No status uploading or pending )
    if (!isUploadingFiles) {
      return t('common.uploadedFilesCounter', {
        amount: uploadedGroupedFilesAmount,
        total: totalGroupedFilesAmount,
      });
    } else {
      return t('common.uploadingFilesCounter', {
        amount: uploadedGroupedFilesAmount,
        total: totalGroupedFilesAmount,
      });
    }
  }, [uploadFilesQueue]);

  // If there are no processes, don't render the drawer
  if (uploadGroupedFilesQueue.length === 0) {
    return null;
  }

  return (
    <div
      id="upload-progress-drawer"
      className={clsx(
        'w-full rounded-md border border-gray-medium bg-white transition-all duration-200 md:w-[400px] lg:w-[500px]',
        isMinimized ? 'h-12' : 'h-80',
      )}
    >
      <div
        onClick={() => setIsMinimized(!isMinimized)}
        className={clsx(
          'flex items-center justify-between rounded-t-md border-b bg-gray-light px-3 py-2',
          isMinimized ? 'border-transparent' : 'border-gray-medium',
        )}
      >
        <div className="text-base font-medium text-blue-dark">{title}</div>
        <div className="flex items-center">
          <Button
            type="text"
            size="small"
            className="flex items-center pl-0"
            icon={
              <Icon
                name="ChevronDownIcon"
                className={clsx(
                  'mt-1 h-5 w-5 transform transition-transform duration-200',
                  isMinimized ? 'rotate-180' : '',
                )}
              />
            }
          />
          <Button
            type="text"
            size="small"
            onClick={() => emptyUploadFilesQueue()}
            disabled={uploadGroupedFilesQueue.length === 0}
            className="flex items-center pl-0"
            icon={<Icon name="XIcon" className="mt-1 h-4 w-4" />}
          />
        </div>
      </div>

      <div className="flex h-full max-h-fit flex-col divide-y divide-gray overflow-y-auto px-3 pb-14">
        {uploadGroupedFilesQueue.map((file) => (
          <div
            key={file.id}
            ref={(el) => (itemRefs.current[file.id] = el)}
            className={clsx(
              'relative flex min-h-[35px] items-center justify-between transition-all duration-200',
            )}
          >
            <div className="flex items-center gap-1">
              <FileIcon
                isFolder={file?.isFolder}
                fileExtension={file.fileExtension ?? ''}
                showExtension={false}
                className="h-5 w-5"
              />
              <div className="flex flex-row items-center gap-2 truncate">
                <span
                  aria-label={file.fileName}
                  onClick={() => {
                    if (file.createdFile) {
                      redirectFileToLocation({ file: file.createdFile as File });
                    }
                  }}
                  className={clsx(
                    'max-w-60 truncate text-sm font-medium text-blue-dark',
                    !!file.createdFile ? 'cursor-pointer hover:underline' : 'pointer-events-none',
                  )}
                >
                  <TruncatedTooltip>{file.fileName}</TruncatedTooltip>
                </span>
                {file.totalFiles > 1 && (
                  <span className="text-xs text-blue-dark">
                    {file.uploadedFiles} of {file.totalFiles}
                  </span>
                )}
                {!!file?.retries && file?.retries > 0 && (
                  <span className="text-xs">
                    ({t('common.retries')}:{file.retries})
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-center">
              {((): ReactNode => {
                switch (file.status) {
                  case FileUploadStatusEnum.Pending:
                    return (
                      <div>
                        <span className="text-xs text-gray-dark">
                          {t('common.waitingForUpload')}
                        </span>
                        <Spin size="small" />
                      </div>
                    );
                  case FileUploadStatusEnum.Uploading:
                    const uploaded = Math.round((file.fileSize * (file.progress ?? 0)) / 100);
                    const uploadSpeed = getUploadSpeed(file.id);
                    const eta = getETA(file.fileSize, uploaded, uploadSpeed);
                    return (
                      <>
                        <div className="flex items-center gap-2">
                          <div className="flex min-w-[160px] flex-col items-end text-xs text-gray-dark">
                            <span>
                              {formatBytes(uploaded)} / {formatBytes(file.fileSize)}
                              {uploadSpeed && <> ({formatBytes(uploadSpeed)}/s)</>}
                            </span>
                            <span>{formatETA(eta)}</span>
                          </div>
                          {/*{file.totalFiles > 1 &&
                            getFailedFilesInFolder(file.id).length <
                              file.totalFiles && (
                              <>
                                <Icon
                                  name="CircleXIcon"
                                  className="h-5 w-5 text-red-dark"
                                />
                              </>
                            )}*/}
                          {/* <Spin size="small" percent={file.progress ?? 0} />
                          <span className="w-9 text-sm">{`${file.progress}%`}</span> */}
                        </div>
                      </>
                    );
                  case FileUploadStatusEnum.Completed:
                    return (
                      <div className="flex items-center">
                        {file.createdFile?.ownerId === authenticatedUser?.id ? (
                          <Tooltip placement="left" title={t('common.share')}>
                            <Button
                              aria-label={t('common.share')}
                              size="small"
                              type="text"
                              onClick={() => {
                                handleShareFiles([file.createdFile as File], {
                                  onFilesCreated: () => {
                                    EventBus.emit(EventBusEventEnum.OnFilesUpdated, {
                                      files: [file.createdFile as File],
                                    });
                                  },
                                });
                              }}
                            >
                              <Icon name="FileUserIcon" className="h-5 w-5 text-blue-dark" />
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip placement="left" title={t('common.copyLink')}>
                            <Button
                              aria-label={t('common.copyLink')}
                              className="px-2 py-0.5"
                              type="text"
                              size="small"
                              onClick={() => {
                                copyToClipboard(
                                  `${window.location.origin}/file/${file?.createdFile?.id}`,
                                )
                                  .then(() => {
                                    notification.success({
                                      message: t('notifications.linkCopiedToClipboard'),
                                    });
                                  })
                                  .catch(() => {
                                    notification.error({
                                      message: t('errors.hitASnag'),
                                    });
                                  });
                              }}
                            >
                              <Icon name="CopyIcon" className="h-5 w-5 text-blue-dark" />
                            </Button>
                          </Tooltip>
                        )}
                        <Icon name="CircleCheckIcon" className="h-5 w-5 text-green-dark" />
                      </div>
                    );
                  case FileUploadStatusEnum.Failed:
                    return (
                      <div className="flex items-center">
                        {file.totalFiles > 1 &&
                        getFailedFilesInFolder(file.id).length < file.totalFiles ? (
                          <>
                            <Button
                              type="text"
                              size="small"
                              onClick={() => {
                                handleOpenUploadingFilesErrorsModal(false);
                              }}
                            >
                              <Icon name="TriangleAlertIcon" className="h-5 w-5 text-red-dark" />
                            </Button>
                            <Icon name="CircleCheckIcon" className="h-5 w-5 text-green-dark" />
                          </>
                        ) : (
                          <Button
                            type="text"
                            size="small"
                            onClick={() => {
                              handleOpenUploadingFilesErrorsModal(false);
                            }}
                          >
                            <div className="flex flex-row items-center">
                              <Icon name="CircleXIcon" className="h-5 w-5 text-red-dark" />
                              <span className="text-sm text-red-dark">{t('common.failed')}</span>
                            </div>
                          </Button>
                        )}
                      </div>
                    );
                  default:
                    return 'N/D';
                }
              })()}
            </div>
            {[
              FileUploadStatusEnum.Uploading,
              FileUploadStatusEnum.Pending,
              FileUploadStatusEnum.Failed,
            ].includes(file.status) && (
              <Progress
                status={file.status === FileUploadStatusEnum.Failed ? 'exception' : 'active'}
                className="compact-progress absolute -bottom-2 left-0 right-0 z-10"
                size="small"
                percent={file.progress ?? 0}
                showInfo={false}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UploadProgressDrawer;
